import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import Select from "react-select";
import {
  addToCart,
  requestGetBusinessService,
  useAuthState,
  useBusinessServiceDispatch,
  useBusinessServiceState,
  useCheckoutDispatch,
} from "../../Context";
import FormLabel from "../../Components/FormLabel";
import {
  classNames,
  convertToGoogleAnalyticsItem,
  trackAddToCart,
} from "../../Utils";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import { Field, Label, Switch } from "@headlessui/react";
import InputSkeleton from "../../Components/InputSkeleton";
import { PhoneIcon } from "@heroicons/react/solid";
import { useRemoteConfig } from "../../Context/firebase/context";
import ConsultantCard from "../../Components/ConsultantCard";

export const BusinessServiceForm = () => {
  const { t } = useTranslation();
  const [fieldError, setfieldError] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const checkoutDispatch = useCheckoutDispatch();

  const dispatch = useBusinessServiceDispatch();
  const { businessService, businessServiceLoading, businessServiceError } =
    useBusinessServiceState();

  const { user } = useAuthState();
  const { service_contact_button: serviceContactButton } = useRemoteConfig();

  const [customerName, setcustomerName] = useState("");
  const [businessName, setbusinessName] = useState("");
  const [phone, setphone] = useState();
  const [email, setemail] = useState("");

  const [customerError, setcustomerError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [emailError, setemailError] = useState(null);
  const [submitted, setsubmitted] = useState(false);
  const [contactEnabled, setcontactEnabled] = useState(false);

  const [chosenCityId, setchosenCityId] = useState("");
  const [citySelectionError, setcitySelectionError] = useState(null);

  const cleanedPhone = phone?.replace(/\s/g, "");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const [switchStates, setSwitchStates] = useState({});

  const provinceGroups = useMemo(() => {
    const serviceAreas = businessService?.serviceAreas?.items;
    if (!serviceAreas || serviceAreas.length === 0) return {};

    const groups = {};
    serviceAreas.forEach((area) => {
      const provinceId = area.city.province.id;
      const provinceName = area.city.province.name;

      if (!groups[provinceId]) {
        groups[provinceId] = {
          provinceName,
          cities: [],
        };
      }

      groups[provinceId].cities.push(area.city);
    });

    return groups;
  }, [businessService?.serviceAreas?.items]);

  const cityOptions = useMemo(() => {
    if (!provinceGroups || Object.keys(provinceGroups).length === 0) return [];

    return Object.entries(provinceGroups).map(([provinceId, group]) => ({
      label: group.provinceName,
      options: group.cities.map((city) => ({
        value: city.id,
        label: city.name,
        provinceName: group.provinceName,
      })),
    }));
  }, [provinceGroups]);

  const selectedCity = useMemo(() => {
    if (!chosenCityId || !cityOptions.length) return null;

    for (const group of cityOptions) {
      const found = group.options.find((opt) => opt.value === chosenCityId);
      if (found) return found;
    }
    return null;
  }, [chosenCityId, cityOptions]);

  const totalPrice = useMemo(() => {
    const addOnsTotal =
      businessService?.addOns?.items?.reduce((sum, addOn) => {
        return switchStates[addOn.addOnID] ? sum + addOn.addOn.price : sum;
      }, 0) || 0;

    return (businessService?.price || 0) + addOnsTotal;
  }, [businessService?.price, businessService?.addOns?.items, switchStates]);

  const totalBasePrice = useMemo(() => {
    const addOnsBaseTotal =
      businessService?.addOns?.items?.reduce((sum, addOn) => {
        if (switchStates[addOn.addOnID]) {
          const basePrice = addOn.addOn.basePrice || addOn.addOn.price;
          return sum + basePrice;
        }
        return sum;
      }, 0) || 0;

    const serviceBasePrice =
      businessService?.basePrice || businessService?.price || 0;
    return serviceBasePrice + addOnsBaseTotal;
  }, [
    businessService?.price,
    businessService?.basePrice,
    businessService?.addOns?.items,
    switchStates,
  ]);

  const selectedAddOns = useMemo(() => {
    if (!businessService?.addOns?.items || !switchStates) {
      return [];
    }

    return businessService.addOns.items
      .filter((item) => switchStates[item.addOnID])
      .map((item) => ({
        id: item.id,
        type: "business-service-addon",
        name: item.addOn.name,
        price: item.addOn.price,
        quantity: 1,
        detail: {
          useGlobalBalance: false,
          description: "Add-On",
        },
      }));
  }, [businessService?.addOns?.items, switchStates]);

  useEffect(() => {
    setcontactEnabled(serviceContactButton === "enabled");
  }, [serviceContactButton]);

  useEffect(() => {
    requestGetBusinessService(dispatch, { id });
  }, [dispatch, id]);

  useEffect(() => {
    if (businessService?.addOns?.items) {
      const initialStates = businessService.addOns.items.reduce((acc, item) => {
        acc[item.addOn.id] = false;
        return acc;
      }, {});
      setSwitchStates(initialStates);
    }
  }, [businessService?.addOns?.items]);

  useEffect(() => {
    if (user) {
      setcustomerName(user.name || customerName);
      setphone(user.phone || phone);
      setemail(user.email || email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (submitted) {
      const isCustomerValid = Boolean(customerName);
      const isPhoneValid = cleanedPhone?.length >= 10;
      const isEmailValid = emailRegex.test(email);

      if (isCustomerValid) {
        setErrorMessage("customer", "");
      } else {
        setErrorMessage("customer", "customerNameRequired");
      }

      if (phone) {
        if (isPhoneValid) {
          setErrorMessage("phone", "");
        } else {
          setErrorMessage("phone", "Please enter a valid phone number.");
        }
      } else {
        setErrorMessage("phone", "whatsAppNumberRequired");
      }

      if (email) {
        if (isEmailValid) {
          setErrorMessage("email", "");
        } else {
          setErrorMessage("email", "Please enter a valid email address.");
        }
      } else {
        setErrorMessage("email", "Email is required.");
      }

      const hasServiceAreas = businessService?.serviceAreas?.items?.length > 0;
      if (hasServiceAreas && !chosenCityId) {
        setcitySelectionError("citySelectionRequired");
      } else {
        setcitySelectionError(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, phone, email, submitted]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "customer":
        setcustomerError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "email":
        setemailError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    const isCustomerValid = Boolean(customerName);
    const isPhoneValid = phone && cleanedPhone?.length >= 10;
    const isEmailValid = email && emailRegex.test(email);
    let isCityValid = true;

    if (businessService?.serviceAreas?.items?.length > 0) {
      isCityValid = Boolean(chosenCityId);
    }

    return isCustomerValid && isPhoneValid && isEmailValid && isCityValid;
  };

  const handleSwitchChange = (id) => {
    setSwitchStates((prevStates) => {
      const updatedStates = {
        ...prevStates,
        [id]: !prevStates[id],
      };

      return updatedStates;
    });
  };

  const handleCheckout = (event) => {
    setsubmitted(true);
    const valid = validateInput();
    event.preventDefault();
    if (valid) {
      setfieldError(false);
      const businessServiceProduct = {
        id: businessService.id,
        type: "business-service",
        name: businessService.name,
        price: businessService.price,
        detail: {
          name: customerName,
          email,
          phone,
          companyName: businessName,
          useGlobalBalance: false,
          description: businessName,
          ...(chosenCityId && { cityID: chosenCityId }),
        },
        quantity: 1,
      };

      const products = [businessServiceProduct, ...selectedAddOns];

      const analyticsParams = {
        currency: "IDR",
        value: products.reduce((total, product) => {
          return total + product.price * product.quantity;
        }, 0),
        items: products.map((product) => convertToGoogleAnalyticsItem(product)),
      };

      logEvent(analytics, "add_to_cart", analyticsParams);
      trackAddToCart({ products: products });

      addToCart(checkoutDispatch, {
        products,
      });

      navigate("/checkout");
    } else {
      setfieldError(true);
    }
  };

  const renderCityOptions = () => {
    if (!cityOptions || cityOptions.length === 0) return null;

    return (
      <div className="mt-4">
        <FormLabel label={t("serviceCity")} required />
        <Select
          value={selectedCity}
          onChange={(option) => {
            setchosenCityId(option?.value || "");
            setcitySelectionError(null);
          }}
          options={cityOptions}
          isSearchable
          isDisabled={isConsultant}
          menuPortalTarget={document.body}
          placeholder={t("selectServiceCity")}
          noOptionsMessage={() => t("cityNotFound")}
          filterOption={(option, inputValue) => {
            const searchValue = inputValue.toLowerCase();
            const cityName = option.label.toLowerCase();
            const provinceName = option.data.provinceName?.toLowerCase() || "";
            return (
              cityName.includes(searchValue) ||
              provinceName.includes(searchValue)
            );
          }}
          formatGroupLabel={(group) => (
            <div className="py-1">
              <span className="text-sm font-semibold text-gray-700">
                {group.label}
              </span>
            </div>
          )}
          styles={{
            groupHeading: (base) => ({
              ...base,
              backgroundColor: "#f9fafb",
              padding: "8px 12px",
              margin: 0,
              borderBottom: "1px solid #e5e7eb",
            }),
          }}
          className="text-sm"
          classNamePrefix="select2-selection"
        />
        {citySelectionError && (
          <div className="mt-2 text-red-600 text-sm">
            {t(citySelectionError)}
          </div>
        )}
      </div>
    );
  };

  const isConsultant = user?.membership === "CONSULTANT";

  if (!businessService || businessServiceError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={businessServiceError?.message || "businessServiceNotFound"}
          errors={businessServiceError?.errors}
        />
      </div>
    );
  }

  if (businessService.status === "UNAVAILABLE") {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert type="error" title="Error" message="serviceNotAvailable" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-8">
      <div className="bg-white border border-gray-100 divide-y divide-gray-100 shadow-sm sm:overflow-hidden rounded-xl lg:col-start-1 lg:col-span-4 transition-all duration-300 hover:shadow-lg">
        <form onSubmit={handleCheckout}>
          <div className="space-y-6 px-4 py-5 sm:p-6">
            {isConsultant && (
              <Alert
                title={t("Consultant Account")}
                type="info"
                message={t(
                  "Consultants cannot order services. This form is for viewing purposes only."
                )}
              />
            )}
            {fieldError && (
              <Alert
                title="Incomplete Form"
                type="warning"
                message="Please fill out all fields."
              />
            )}
            {businessServiceError && (
              <Alert
                title="Error"
                type="error"
                message={businessServiceError?.message || "Error create forum."}
                errors={businessServiceError?.errors}
              />
            )}

            {businessServiceLoading ? (
              <InputSkeleton rows={4} />
            ) : (
              <>
                <div>
                  <FormLabel
                    htmlFor="customer"
                    label={t("customerName")}
                    required
                  />
                  <input
                    type="text"
                    name="customer"
                    id="customer"
                    data-testid="customer"
                    value={customerName}
                    onChange={(e) => setcustomerName(e.target.value)}
                    placeholder={t("enterCustomerName")}
                    disabled={isConsultant}
                    className={classNames(
                      customerError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-600 focus:ring-blue-600",
                      isConsultant && "bg-gray-50 cursor-not-allowed",
                      "mt-1 block w-full text-sm rounded-md shadow-sm transition-colors duration-200"
                    )}
                  />
                  {customerError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(customerError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="business"
                    label={t("businessEntityName")}
                  />
                  <input
                    type="text"
                    name="business"
                    id="business"
                    data-testid="business"
                    value={businessName}
                    onChange={(e) => setbusinessName(e.target.value)}
                    placeholder={t("enterBusinessEntityName")}
                    disabled={isConsultant}
                    className={classNames(
                      isConsultant && "bg-gray-50 cursor-not-allowed",
                      "border-gray-300 focus:border-blue-600 focus:ring-blue-600 mt-1 block w-full text-sm rounded-md shadow-sm transition-colors duration-200"
                    )}
                  />
                </div>
                <div>
                  <FormLabel
                    htmlFor="phone"
                    label={t("whatsappNumber")}
                    required
                  />
                  <FormPhoneInput
                    id="phone"
                    name="phone"
                    data-testid="phone"
                    value={phone}
                    onChange={setphone}
                    placeholder={t("enterWhatsAppNumber")}
                    disabled={isConsultant}
                    className={classNames(
                      phoneError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-600 focus:ring-blue-600",
                      isConsultant && "bg-gray-50 cursor-not-allowed",
                      "mt-1 block w-full text-sm rounded-md shadow-sm transition-colors duration-200"
                    )}
                  />
                  {phoneError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(phoneError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="email" label={t("Email")} required />
                  <input
                    type="email"
                    name="email"
                    data-testid="email"
                    value={email}
                    placeholder={t("Enter email")}
                    onChange={(e) => setemail(e.target.value)}
                    disabled={isConsultant}
                    className={classNames(
                      emailError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-600 focus:ring-blue-600",
                      isConsultant && "bg-gray-50 cursor-not-allowed",
                      "mt-1 block w-full text-sm rounded-md shadow-sm transition-colors duration-200"
                    )}
                  />
                  {emailError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(emailError)}
                    </div>
                  )}
                </div>
                {renderCityOptions()}
                {businessService?.addOns?.items?.length > 0 && (
                  <div>
                    <FormLabel htmlFor="phone" label={t("addsOns")} />
                    {businessService?.addOns?.items.map((item, index) => {
                      const addOnDetail = item.addOn;
                      const isChecked = switchStates[addOnDetail.id] || false;

                      return (
                        <Field
                          as="div"
                          className={classNames(
                            index !== 0
                              ? "border-t-0 rounded-b-lg"
                              : "rounded-t-lg mt-1",
                            index === businessService.addOns.items.length - 1 &&
                              "rounded-b-lg",
                            "flex border border-gray-200 items-center py-3 px-4 transition-colors duration-200 hover:bg-gray-50"
                          )}
                          key={item.id}
                        >
                          <Switch
                            checked={isChecked}
                            onChange={() => handleSwitchChange(addOnDetail.id)}
                            disabled={businessServiceError || isConsultant}
                            className={classNames(
                              isChecked ? "bg-blue-600" : "bg-gray-200",
                              "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0"
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={classNames(
                                isChecked ? "translate-x-5" : "translate-x-0",
                                businessServiceError
                                  ? "bg-gray-300"
                                  : "bg-white",
                                "pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200"
                              )}
                            />
                          </Switch>
                          <Label as="span" className="ml-3">
                            <div className="flex flex-col">
                              <span
                                className={classNames(
                                  businessServiceError
                                    ? "text-gray-300"
                                    : "text-gray-900",
                                  "text-sm font-medium text-gray-900"
                                )}
                              >
                                {t(addOnDetail.name)}
                              </span>
                              <span
                                className={classNames(
                                  businessServiceError
                                    ? "text-gray-300"
                                    : "text-gray-900",
                                  "text-sm font-normal text-gray-900"
                                )}
                              >
                                {t(addOnDetail.description)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-blue-600">
                                <NumericFormat
                                  displayType={"text"}
                                  value={addOnDetail.price}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </span>
                              {addOnDetail.basePrice &&
                                addOnDetail.basePrice > addOnDetail.price && (
                                  <>
                                    <span className="text-xs font-medium text-gray-400 line-through">
                                      <NumericFormat
                                        displayType={"text"}
                                        value={addOnDetail.basePrice}
                                        thousandSeparator={"."}
                                        decimalSeparator={","}
                                        prefix={"Rp"}
                                      />
                                    </span>
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                                      -
                                      {Math.round(
                                        ((addOnDetail.basePrice -
                                          addOnDetail.price) /
                                          addOnDetail.basePrice) *
                                          100
                                      )}
                                      %
                                    </span>
                                  </>
                                )}
                            </div>
                          </Label>
                        </Field>
                      );
                    })}
                  </div>
                )}
                {contactEnabled && (
                  <div>
                    <label
                      htmlFor="customer-service"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("stillConfusedAboutChoosingService")}
                    </label>

                    <div className="border border-gray-300 py-4 px-4 sm:px-6 rounded-md shadow-sm mt-1">
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href =
                            "https://s.id/serviceconsultation";
                        }}
                        className="w-full block font-medium text-gray-700 text-sm sm:text-base"
                      >
                        <div className="flex gap-4 items-center">
                          <PhoneIcon className="flex-none h-6 w-6" />
                          <div className="flex flex-col">
                            <div className="flex-1 text-left text-sm font-medium text-gray-900">
                              {t("businessServiceConsultation")}
                            </div>
                            <span className="text-left text-sm font-normal text-gray-900">
                              {t("Free")}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-4 sm:p-6 flex flex-col gap-4 justify-between bg-blue-50 rounded-b-xl sm:flex-row sm:items-center border-t border-gray-100">
            <div>
              <p className="text-sm text-slate-500">{t("Total")}</p>
              {businessServiceLoading ? (
                <div className="animate-pulse h-3 mt-[2px] mb-[2px] w-16 bg-gray-300 rounded-md"></div>
              ) : (
                <div className="flex items-baseline gap-2">
                  <p className="text-sm font-medium">
                    <NumericFormat
                      displayType={"text"}
                      value={totalPrice}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </p>
                  {totalBasePrice > totalPrice && (
                    <p className="text-sm font-medium text-gray-400 line-through">
                      <NumericFormat
                        displayType={"text"}
                        value={totalBasePrice}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    </p>
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              loading={businessServiceLoading}
              disabled={
                businessServiceLoading ||
                businessServiceError ||
                businessService.status === "UNAVAILABLE" ||
                isConsultant
              }
              className="w-full sm:w-auto"
              title={t("Order")}
              data-testid="business-service-checkout"
            />
          </div>
        </form>
      </div>

      {/* Consultant/Service Provider Card */}
      {businessService?.consultant && (
        <div className="lg:col-start-5 lg:col-span-4">
          <ConsultantCard
            consultant={businessService.consultant}
            serviceName={businessService.name}
            serviceDescription={businessService.description}
          />
        </div>
      )}
    </div>
  );
};
